"use client";

import { useState, useRef } from "react";
import {
  ChevronDown,
  Menu,
  X,
  Globe,
  MessageCircle,
  ChevronLeft,
  ChevronRight,
  ThumbsUp,
  AlertTriangle,
  Search,
  AlertCircle,
} from "lucide-react";

export const MountKKLanding = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeAccordion, setActiveAccordion] = useState(null);
  const [activeItineraryDay, setActiveItineraryDay] = useState(null);
  const [language, setLanguage] = useState("english");
  const [isLanguageMenuOpen, setIsLanguageMenuOpen] = useState(false);
  const [isEngineerMode] = useState(false); // Set to true only for engineers/developers
  const [lightboxImage, setLightboxImage] = useState(null);
  const [currentCarouselIndex, setCurrentCarouselIndex] = useState(0);
  const carouselRef = useRef(null);

  // Mountain images for carousel
  const carouselImages = [
    {
      src: "https://images.unsplash.com/photo-1586339949916-3e9457bef6d3?q=80&w=640&h=360&auto=format&fit=crop",
      alt: "Mount Kinabalu Image 1",
      link: "#image1",
    },
    {
      src: "https://images.unsplash.com/photo-1613310023042-ad79320c00ff?q=80&w=640&h=360&auto=format&fit=crop",
      alt: "Mount Kinabalu Image 2",
      link: "#image2",
    },
    {
      src: "https://images.unsplash.com/photo-1544198365-f5d60b6d8190?q=80&w=640&h=360&auto=format&fit=crop",
      alt: "Mount Kinabalu Image 3",
      link: "#image3",
    },
    {
      src: "https://images.unsplash.com/photo-1605540436563-5bca919ae766?q=80&w=640&h=360&auto=format&fit=crop",
      alt: "Mount Kinabalu Image 4",
      link: "#image4",
    },
    {
      src: "https://images.unsplash.com/photo-1542332213-9b5a5a3fad35?q=80&w=640&h=360&auto=format&fit=crop",
      alt: "Mount Kinabalu Image 5",
      link: "#image5",
    },
    {
      src: "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?q=80&w=640&h=360&auto=format&fit=crop",
      alt: "Mount Kinabalu Image 6",
      link: "#image6",
    },
    {
      src: "https://images.unsplash.com/photo-1519681393784-d120267933ba?q=80&w=640&h=360&auto=format&fit=crop",
      alt: "Mount Kinabalu Image 7",
      link: "#image7",
    },
    {
      src: "https://images.unsplash.com/photo-1483728642387-6c3bdd6c93e5?q=80&w=640&h=360&auto=format&fit=crop",
      alt: "Mount Kinabalu Image 8",
      link: "#image8",
    },
  ];

  const toggleAccordion = (index) => {
    setActiveAccordion(activeAccordion === index ? null : index);
  };

  const toggleItineraryDay = (index) => {
    setActiveItineraryDay(activeItineraryDay === index ? null : index);
  };

  const toggleLanguageMenu = () => {
    setIsLanguageMenuOpen(!isLanguageMenuOpen);
  };

  const changeLanguage = (lang) => {
    setLanguage(lang);
    setIsLanguageMenuOpen(false);
  };

  const navigateCarousel = (direction) => {
    if (direction === "next") {
      setCurrentCarouselIndex((prev) => (prev + 1) % 2); // Toggle between 0 and 1 for 8 images (4 per page)
    } else {
      setCurrentCarouselIndex((prev) => (prev === 0 ? 1 : 0)); // Toggle between 0 and 1 for 8 images (4 per page)
    }
  };

  const itineraryData = [
    {
      day: "Day 1",
      title: "Arrival & Preparation",
      description: [
        "Meet at Airport, Van pick up and depart to kundasang",
        "Replenish esentials at mart",
        "3 hours drive to accommodation near to the foot of Mt.Kinabalu",
        "Check in accommodation, rest, dinner, tour leader briefing",
      ],
    },
    {
      day: "Day 2",
      title: "The Climb",
      description: [
        "Get ready, have breakfast & pack your bags",
        "Shuttle van pick up to Kinabalu Park",
        "Registered, head to timpohon gate to start hiking",
        "Lunch at rest point",
        "Check in Panalaban/Lemaing Hostel",
        "Enjoy dinner, easy and chill, rest",
      ],
    },
    {
      day: "Day 3",
      title: "Summit & Descent",
      description: [
        "Get ready, pack your summit bag, warm up",
        "Supper, gather and briefing, Start Hiking",
        "Arrived Sayat-Sayat check point",
        "Summit Low's Peak",
        "Return to half peak accommodations",
        "Enjoy Breakfast, check out, start to descent to Timpohon Gate",
        "Arrive Kinabalu Park, van depart to KK town",
        "Arrive at airport, trip end",
      ],
    },
  ];

  const faqs = [
    {
      question: "What is the success rate of getting a permit?",
      answer:
        "The success rate varies depending on the season. During peak seasons (June-August), it can be as low as 60%. We recommend booking early to increase your chances.",
    },
    {
      question: "When should I book my flight tickets?",
      answer:
        "We recommend booking your flight tickets only after your permit is confirmed, which typically happens 2-3 months before your climb date.",
    },
    {
      question: "What is included in the package?",
      answer:
        "Our package includes climbing permits, mountain guide, accommodation at Laban Rata, meals during the climb, and transportation from Kota Kinabalu to the park entrance.",
    },
    {
      question: "How physically demanding is the climb?",
      answer:
        "The climb is moderately challenging and requires a good level of fitness. The total distance is about 8.7km with an elevation gain of 2,300m.",
    },
    {
      question: "What happens if my permit application is unsuccessful?",
      answer:
        "If your permit application is unsuccessful, we offer a full refund or the option to reschedule for a different date.",
    },
  ];

  const testimonials = [
    {
      name: "Snowy Aw",
      rating: 5,
      text: "Good experience mount climbing trip with Avider. Well arrangements, team leader Zhu Hang is helpful and friendly, team members are nice. Hope to join others trip in future!.",
      image: "/images/profile-snowy.png",
    },
    {
      name: "Htet Htet",
      rating: 5,
      text: "I recently joined a solo trip with Avider for the Mount Kinabalu hike in November 2024, and it was an unforgettable experience. This was my first time hiking with Avider, and I had so much fun while gaining incredible memories along the way",
      image: "/images/profile-htet.png",
    },
    {
      name: "Janice Chung",
      rating: 5,
      text: "Great trip with Avider travel. Every details was clearly informed. Beautiful photos taken by their photographer cum guide. 😉 highly recommend Avider for adventures hiking trip. Will choose them again for my next trip. 👍",
      image: "/images/profile-janice.png",
    },
    {
      name: "Kevin Khor",
      rating: 5,
      text: "An enjoyable journey with their Mount KK hiking trip. From starting enquiries to booking and waiting for the hiking day to come, they have being very informative, responsive and helpfull solving all the doubts of any you can think of it. When arriving KK,  the guide, Yuan has been very well care of all of us and also being very supportive, funny, informative and full with energy to guide us up to the top. They really are awesome and passionate hiking maniac.",
      image: "/images/profile-kevin.png",
    },
  ];

  const usps = [
    {
      title: "Team Leader Accompaniment",
      description:
        "Our local team leader is Chinese and English communication, leader will accompany you throughout the entire journey including hiking",
      image: "/images/usp-3.jpeg", // Image 3
    },
    {
      title: "Photography Service",
      description:
        "We will guide you to the best spots for capturing memorable photos,No additional charges",
      image: "/images/usp-2.jpeg", // Image 2
    },
    {
      title: "Open Group Participation",
      description:
        "Solo travellers are welcome to join our group,meet new friends and complete the journey together",
      image: "/images/usp-1.jpeg", // Image 1
    },
    {
      title: "Handling Miscellaneous Matters",
      description:
        "Sit back, relax, and let us take care of all the details. You just enjoy the journey",
      image: "/images/usp-4.jpeg", // Image 4
    },
  ];

  return (
    <div className="font-['Manrope'] tw-text-black tw-bg-white tw-min-h-screen">
      {/* Navigation - UPDATED language switcher position and logo size */}
      <header className="tw-container tw-mx-auto tw-py-4 tw-px-6 lg:tw-px-16 tw-flex tw-justify-between tw-items-center">
        <div className="tw-flex tw-items-center tw-space-x-2">
          <img
            src="https://hebbkx1anhila5yf.public.blob.vercel-storage.com/aivder_en_logo-XpS3n7yomueWVHMDVer89EI55JtGgF.png"
            alt="AVIDER English Logo"
            className="tw-h-6 tw-w-auto"
          />
          <img
            src="https://hebbkx1anhila5yf.public.blob.vercel-storage.com/avider_cn_logo-JY5Af2jbdrgA9tnWBS0z0vLwcRDlCF.png"
            alt="AVIDER Chinese Logo"
            className="tw-h-6 tw-w-auto"
          />
        </div>

        <div className="tw-flex tw-items-center">
          {/* Language Selector - Positioned at top right */}
          <div className="tw-relative">
            <button
              onClick={toggleLanguageMenu}
              className="tw-flex tw-items-center tw-space-x-1 tw-p-2 hover:tw-bg-gray-100 tw-rounded-md"
            >
              <Globe size={18} />
              <span className="tw-hidden md:tw-inline">
                {language === "english"
                  ? "English"
                  : language === "simplified"
                  ? "中文简体"
                  : "繁體中文"}
              </span>
            </button>

            {isLanguageMenuOpen && (
              <div className="tw-absolute tw-right-0 tw-mt-2 tw-w-48 tw-bg-white tw-rounded-md tw-shadow-lg tw-z-50">
                <div className="tw-py-1">
                  <button
                    onClick={() => changeLanguage("english")}
                    className="tw-block tw-w-full tw-text-left tw-px-4 tw-py-2 tw-text-sm hover:tw-bg-gray-100"
                  >
                    English
                  </button>
                  <button
                    onClick={() => changeLanguage("simplified")}
                    className="tw-block tw-w-full tw-text-left tw-px-4 tw-py-2 tw-text-sm hover:tw-bg-gray-100"
                  >
                    中文简体
                  </button>
                  <button
                    onClick={() => changeLanguage("traditional")}
                    className="tw-block tw-w-full tw-text-left tw-px-4 tw-py-2 tw-text-sm hover:tw-bg-gray-100"
                  >
                    繁體中文
                  </button>
                </div>
              </div>
            )}
          </div>

          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="md:tw-hidden tw-p-2 tw-ml-2"
          >
            {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </header>

      {/* Hero Section with Campaign Poster Image - UPDATED with black background and yellow text */}
      <section className="tw-bg-black tw-py-16">
        <div className="tw-container tw-mx-auto tw-px-6 lg:tw-px-16 tw-text-center">
          <h2 className="tw-text-[#FFD700] tw-text-3xl tw-font-bold tw-mb-8">
            Mount Kinabalu 2026 Pre-Book is live!
          </h2>

          <div className="tw-max-w-3xl tw-mx-auto tw-relative">
            <div className="tw-aspect-[3/2] tw-relative">
              <img
                src="/images/mountain-summit.jpeg"
                alt="Mount Kinabalu 2026 Campaign Poster"
                className="tw-object-cover tw-rounded-lg tw-w-full tw-h-full"
              />
              {isEngineerMode && (
                <>
                  <label
                    htmlFor="hero-image-upload"
                    className="tw-absolute tw-inset-0 tw-cursor-pointer tw-flex tw-items-center tw-justify-center tw-opacity-0 hover:tw-opacity-50 tw-bg-black tw-text-white tw-transition-opacity"
                  >
                    Click to upload image (2700 x 1800 pixels)
                  </label>
                  <input
                    type="file"
                    id="hero-image-upload"
                    accept="image/*"
                    className="tw-hidden"
                  />
                </>
              )}
            </div>
            <p className="tw-text-white tw-text-lg tw-max-w-3xl tw-mx-auto tw-mt-6 tw-mb-8">
              Mount Kinabalu is the highest mountain in Malaysia and a UNESCO
              World Natural Heritage site. It's a dream climb for many
              Malaysians and a bucket list adventure for travelers from around
              the world. A mountain you must visit at least once in your
              lifetime — elevate your life to new heights! Altitude: 4,095
              meters.
            </p>
            {/* Image Carousel - UPDATED with navigation arrows and real mountain images */}
            <div className="tw-mt-12 tw-max-w-5xl tw-mx-auto tw-relative">
              <div
                className="tw-grid tw-grid-cols-4 tw-gap-4"
                ref={carouselRef}
              >
                {carouselImages
                  .slice(currentCarouselIndex * 4, currentCarouselIndex * 4 + 4)
                  .map((image, index) => (
                    <div
                      key={index + currentCarouselIndex * 4}
                      onClick={() => setLightboxImage(image)}
                      className="tw-cursor-pointer tw-aspect-video tw-relative tw-rounded-lg tw-overflow-hidden hover:tw-opacity-90 tw-transition-opacity"
                    >
                      <img
                        src={image.src || "/placeholder.svg"}
                        alt={image.alt}
                        className="tw-object-cover tw-w-full tw-h-full"
                      />
                    </div>
                  ))}
              </div>

              {/* Navigation Arrows */}
              <button
                onClick={() => navigateCarousel("prev")}
                className="tw-absolute tw-left-0 tw-top-1/2 -tw-translate-y-1/2 -tw-translate-x-4 tw-bg-[#FFD700] tw-p-2 tw-rounded-full tw-shadow-md hover:tw-bg-yellow-400 tw-transition-colors"
                aria-label="Previous images"
              >
                <ChevronLeft size={24} className="tw-text-black" />
              </button>
              <button
                onClick={() => navigateCarousel("next")}
                className="tw-absolute tw-right-0 tw-top-1/2 -tw-translate-y-1/2 tw-translate-x-4 tw-bg-[#FFD700] tw-p-2 tw-rounded-full tw-shadow-md hover:tw-bg-yellow-400 tw-transition-colors"
                aria-label="Next images"
              >
                <ChevronRight size={24} className="tw-text-black" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* About Mount KK Section */}
      <section className="tw-py-16 tw-border-b tw-border-gray-100">
        <div className="tw-container tw-mx-auto tw-px-6 lg:tw-px-16">
          <h2 className="tw-text-2xl tw-font-bold tw-text-center tw-mb-12">
            Mount Kinabalu, why have to pre-book early?
          </h2>
          <h3 className="tw-text-[#F44336] tw-text-xl tw-font-medium tw-text-center tw-mb-8">
            Avider's pre-booking for the annual Mount Kinabalu is now open!
          </h3>

          <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-12 tw-items-center">
            <div className="tw-aspect-video tw-relative tw-rounded-lg tw-overflow-hidden">
              <img
                src="https://images.unsplash.com/photo-1578301978693-85fa9c0320b9?q=80&w=1080&h=720&auto=format&fit=crop"
                alt="Mount Kinabalu"
                className="tw-object-cover tw-w-full tw-h-full"
              />
              {isEngineerMode && (
                <>
                  <label
                    htmlFor="about-image-upload"
                    className="tw-absolute tw-inset-0 tw-cursor-pointer tw-flex tw-items-center tw-justify-center tw-opacity-0 hover:tw-opacity-50 tw-bg-black tw-text-white tw-transition-opacity"
                  >
                    Click to upload image/video
                  </label>
                  <input
                    type="file"
                    id="about-image-upload"
                    accept="image/*,video/*"
                    className="tw-hidden"
                  />
                </>
              )}
            </div>
            <div>
              <p className="tw-mb-4 tw-text-gray-700">
                Every year, reservations for Mount Kinabalu are sold out
                instantly. If you book right before departure, most dates are
                gone. That's why you need to book early.
              </p>
              <p className="tw-text-red-600">
                Limited to 50 slot per climbing day, and pre-booking is only for
                Mount Kinabalu 3D2n(sabahpark) 3D2N Package.{" "}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Organizer Section */}
      <section className="tw-py-16 tw-border-b tw-border-gray-100 tw-bg-black tw-text-white">
        <div className="tw-container tw-mx-auto tw-px-6 lg:tw-px-16">
          <h2 className="tw-text-2xl tw-font-bold tw-text-center tw-mb-12 tw-text-white">
            Who is organizer? Who are we?
          </h2>
          <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-12 tw-items-center">
            <div className="tw-aspect-video tw-relative tw-rounded-lg tw-overflow-hidden">
              <img
                src="https://images.unsplash.com/photo-1527525443983-6e60c75fff46?q=80&w=1080&h=720&auto=format&fit=crop"
                alt="Organizer Team"
                className="tw-object-cover tw-w-full tw-h-full"
              />
              {isEngineerMode && (
                <>
                  <label
                    htmlFor="organizer-image-upload"
                    className="tw-absolute tw-inset-0 tw-cursor-pointer tw-flex tw-items-center tw-justify-center tw-opacity-0 hover:tw-opacity-50 tw-bg-black tw-text-white tw-transition-opacity"
                  >
                    Click to upload image
                  </label>
                  <input
                    type="file"
                    id="organizer-image-upload"
                    accept="image/*"
                    className="tw-hidden"
                  />
                </>
              )}
            </div>
            <div>
              <h3 className="tw-text-xl tw-font-bold tw-mb-4 tw-text-[#FFD700]">
                We are Avider | Malaysia Hiking Tour Organizer{" "}
              </h3>
              <p className="tw-text-white">
                Lead more then 9,500 people on hiking & trekking trips and
                organized over 500+ tours in the part 6 years.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* USP Section */}
      <section className="tw-py-16 tw-border-b tw-border-gray-100 tw-bg-gray-50">
        <div className="tw-container tw-mx-auto tw-px-6 lg:tw-px-16">
          <h2 className="tw-text-2xl tw-font-bold tw-text-center tw-mb-2">
            Why choose Avider?
          </h2>
          <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 lg:tw-grid-cols-4 tw-gap-6 tw-mt-12">
            {usps.map((usp, index) => (
              <div
                key={index}
                className="tw-p-6 tw-text-center tw-flex tw-flex-col tw-items-center"
              >
                <div className="tw-w-40 tw-h-40 tw-mb-4 tw-relative tw-rounded-full tw-overflow-hidden tw-border-4 tw-border-white tw-shadow-lg tw-mx-auto">
                  <img
                    src={usp.image || "/placeholder.svg"}
                    alt={usp.title}
                    className="tw-object-cover tw-w-full tw-h-full"
                  />
                  {isEngineerMode && (
                    <>
                      <label
                        htmlFor={`usp-image-upload-${index}`}
                        className="tw-absolute tw-inset-0 tw-cursor-pointer tw-flex tw-items-center tw-justify-center tw-opacity-0 hover:tw-opacity-50 tw-bg-black tw-text-white tw-rounded-lg tw-transition-opacity"
                      >
                        Change
                      </label>
                      <input
                        type="file"
                        id={`usp-image-upload-${index}`}
                        accept="image/*"
                        className="tw-hidden"
                      />
                    </>
                  )}
                </div>
                <h3 className="tw-font-bold tw-mb-2">{usp.title}</h3>
                <p className="tw-text-gray-700">{usp.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section - UPDATED with black background boxes and oval-bracketed stars */}
      <section className="tw-py-16 tw-border-b tw-border-gray-100">
        <div className="tw-container tw-mx-auto tw-px-6 lg:tw-px-16">
          <h2 className="tw-text-2xl tw-font-bold tw-text-center tw-mb-12">
            <a
              href="https://www.google.com/maps/place/Avider+Adventure+Travel+Sdn+Bhd/@5.2776897,100.4657471,17z/data=!3m1!5s0x304ab7bbf320900d:0x5f0a5a0a48c34727!4m16!1m9!3m8!1s0xaf8c2b46bb87fbd5:0xe3c10ebb609b4458!2sAvider+Adventure+Travel+Sdn+Bhd!8m2!3d5.2776897!4d100.468322!9m1!1b1!16s%2Fg%2F11sk4m5h7c!3m5!1s0xaf8c2b46bb87fbd5:0xe3c10ebb609b4458!8m2!3d5.2776897!4d100.468322!16s%2Fg%2F11sk4m5h7c?authuser=0&entry=ttu&g_ep=EgoyMDI1MDQxMy4wIKXMDSoASAFQAw%3D%3D"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:tw-text-[#00bfff] tw-transition-colors"
            >
              Google review & customer review
            </a>
          </h2>
          <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 lg:tw-grid-cols-4 tw-gap-6">
            {testimonials.map((testimonial, index) => (
              <div
                key={index}
                className="tw-p-6 tw-border tw-border-gray-200 tw-rounded-lg tw-bg-black tw-text-white"
              >
                <div className="tw-flex tw-items-center tw-mb-4">
                  <div className="tw-w-10 tw-h-10 tw-rounded-full tw-mr-3 tw-relative tw-overflow-hidden">
                    <img
                      src={testimonial.image || "/placeholder.svg"}
                      alt={`${testimonial.name} profile`}
                      className="tw-object-cover tw-w-full tw-h-full"
                    />
                  </div>
                  <div>
                    <p className="tw-font-bold">{testimonial.name}</p>
                    <div className="tw-flex tw-items-center tw-bg-gray-800 tw-rounded-full tw-px-3 tw-py-1 tw-mt-1">
                      {[...Array(5)].map((_, i) => (
                        <span key={i} className="tw-text-[#ffff00]">
                          ★
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
                <p className="tw-text-white tw-text-sm">{testimonial.text}</p>
              </div>
            ))}
          </div>
          <div className="tw-text-center tw-mt-6">
            <a
              href="https://maps.app.goo.gl/d41t8BxzfxAjhLbH9"
              target="_blank"
              rel="noopener noreferrer"
              className="tw-text-gray-600 hover:tw-text-[#00bfff] tw-transition-colors"
            >
              For more reviews,{" "}
              <span className="tw-underline tw-font-medium">click here</span>
            </a>
          </div>
        </div>
      </section>

      {/* YouTuber Section */}
      <section className="tw-py-16 tw-border-b tw-border-gray-100 tw-bg-black tw-text-white">
        <div className="tw-container tw-mx-auto tw-px-6 lg:tw-px-16">
          <h2 className="tw-text-2xl tw-font-bold tw-text-center tw-mb-6 tw-text-white">
            Youtuber collaboration & experience
          </h2>
          <p className="tw-text-center tw-text-[#FFD700] tw-mb-12 tw-font-medium">
            Experience Mount Kinabalu through their eyes
          </p>
          <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-8 tw-mb-8">
            <div className="tw-aspect-video">
              <iframe
                width="100%"
                height="100%"
                src="https://www.youtube.com/embed/BdRD-0m79_o"
                title="Mount Kinabalu Video"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="tw-rounded-lg"
              ></iframe>
            </div>
            <div className="tw-aspect-video">
              <iframe
                width="100%"
                height="100%"
                src="https://www.youtube.com/embed/HeHGuEflu_w"
                title="Mount Rinjani Video"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="tw-rounded-lg"
              ></iframe>
            </div>
          </div>
          <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-8">
            <div className="tw-aspect-video">
              <iframe
                width="100%"
                height="100%"
                src="https://www.youtube.com/embed/RUQckeK7PG0"
                title="CLIMBING MOUNT KINABALU | The Ultimate Documentary GUIDE"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="tw-rounded-lg"
              ></iframe>
            </div>
            <div className="tw-aspect-video">
              <iframe
                width="100%"
                height="100%"
                src="https://www.youtube.com/embed/DfLVMHBtSEg"
                title="Mount Kinabalu Climbing Experience"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="tw-rounded-lg"
              ></iframe>
            </div>
          </div>
        </div>
      </section>

      {/* NEW SECTION: Additional Video Section - UPDATED with smaller video */}
      <section className="tw-py-16 tw-border-b tw-border-gray-100 tw-bg-gray-50">
        <div className="tw-container tw-mx-auto tw-px-6 lg:tw-px-16">
          <h2 className="tw-text-2xl tw-font-bold tw-text-center tw-mb-12">
            Trip info
          </h2>

          {/* Video Box - UPDATED to be medium sized */}
          <div className="tw-max-w-2xl tw-mx-auto tw-mb-8">
            <div className="tw-aspect-video tw-relative">
              <iframe
                width="100%"
                height="100%"
                src="https://www.youtube.com/embed/53rt_I5QPJk?start=18"
                title="Climbing Experience Video"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="tw-rounded-lg"
              ></iframe>
              {isEngineerMode && (
                <>
                  <label
                    htmlFor="experience-video-upload"
                    className="tw-absolute tw-inset-0 tw-cursor-pointer tw-flex tw-items-center tw-justify-center tw-opacity-0 hover:tw-opacity-50 tw-bg-black tw-text-white tw-transition-opacity"
                  >
                    Click to change video URL
                  </label>
                  <input
                    type="file"
                    id="experience-video-upload"
                    accept="video/*"
                    className="tw-hidden"
                  />
                </>
              )}
            </div>
          </div>

          {/* Trip Highlights */}
          <div className="tw-max-w-4xl tw-mx-auto tw-mb-12">
            <h3 className="tw-text-xl tw-font-bold tw-mb-4 tw-text-center">
              Trip Highlights
            </h3>
            <div className="tw-bg-white tw-p-6 tw-rounded-lg tw-shadow-sm">
              <ul className="tw-list-disc tw-pl-5 tw-space-y-2">
                <li>Summit the highest mountain (4,095m) in Malaysia</li>
                <li>Receive the summit certificate of Mount Kinabalu</li>
                <li>Complete the journey with your teammates</li>
              </ul>
            </div>
          </div>

          {/* NEW: Itinerary Highlight Accordion */}
          <div className="tw-max-w-4xl tw-mx-auto tw-mb-12">
            <h3 className="tw-text-xl tw-font-bold tw-mb-6 tw-text-center">
              Itinerary Highlight
            </h3>
            <div className="tw-space-y-4">
              {itineraryData.map((item, index) => (
                <div
                  key={index}
                  className="tw-border tw-border-gray-200 tw-rounded-lg tw-overflow-hidden"
                >
                  <button
                    onClick={() => toggleItineraryDay(index)}
                    className="tw-w-full tw-flex tw-justify-between tw-items-center tw-p-4 tw-bg-black tw-text-white tw-text-left"
                  >
                    <span className="tw-font-medium">
                      {item.day} - {item.title}
                    </span>
                    <ChevronDown
                      className={`tw-transition-transform ${
                        activeItineraryDay === index ? "tw-rotate-180" : ""
                      }`}
                      size={20}
                    />
                  </button>
                  {activeItineraryDay === index && (
                    <div className="tw-p-4 tw-bg-white tw-border-t tw-border-gray-200">
                      <ul className="tw-list-disc tw-pl-5 tw-space-y-2">
                        {Array.isArray(item.description) ? (
                          item.description.map((point, i) => (
                            <li key={i} className="tw-text-gray-700">
                              {point}
                            </li>
                          ))
                        ) : (
                          <li className="tw-text-gray-700">
                            {item.description}
                          </li>
                        )}
                      </ul>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Package Include & Exclude */}
          <div className="tw-max-w-4xl tw-mx-auto">
            <h3 className="tw-text-xl tw-font-bold tw-mb-6 tw-text-center">
              Package Include & Exclude
            </h3>
            <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-6">
              {/* Include Box */}
              <div className="tw-bg-white tw-p-6 tw-rounded-lg tw-shadow-sm">
                <h4 className="tw-font-bold tw-mb-4 tw-flex tw-items-center">
                  <span className="tw-text-green-500 tw-mr-2 tw-text-xl">
                    ✓
                  </span>
                  <span>Include</span>
                </h4>
                <ul className="tw-list-disc tw-pl-5 tw-space-y-2">
                  <li>
                    Accommodations (Day 1 near to the foot of Mt.Kinabalu, Day 2
                    Panalaban / Lemaing)
                  </li>
                  <li>
                    Food (Day 1 Dinner, Day 2 B/L/D, Day 3 Supper, Breakfast,
                    Late Brunch)
                  </li>
                  <li>
                    Transport (BKI Airport &gt; Timpohon Gate &gt; BKI Airport)
                  </li>
                  <li>
                    Guide, international climbing permit, certificate, entry
                    ticket, insurance, event t-shirt
                  </li>
                  <li>Accompanied by tour leader</li>
                  <li>Exclusivet & Service</li>
                </ul>
              </div>

              {/* Exclude Box */}
              <div className="tw-bg-white tw-p-6 tw-rounded-lg tw-shadow-sm">
                <h4 className="tw-font-bold tw-mb-4 tw-flex tw-items-center">
                  <span className="tw-text-red-500 tw-mr-2 tw-text-xl">✗</span>
                  <span>Exclude</span>
                </h4>
                <ul className="tw-list-disc tw-pl-5 tw-space-y-2">
                  <li>Round-trip flight tickets to/from (BKI)</li>
                  <li>Personal travel insurance & expenses</li>
                  <li>
                    Accommodation, transportation, etc (outside of the itinerary
                    package)
                  </li>
                  <li>Porter service & Gear Rental (optional)</li>
                  <li>
                    Overtime fees for mountain guides for late descent and any
                    rescue charges.
                  </li>
                  <li>Tips for guides,tour leader,porters</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Gift & Service Section - MOVED below Package Include & Exclude */}
      <section className="tw-py-16 tw-border-b tw-border-gray-100 tw-bg-black tw-text-white">
        <div className="tw-container tw-mx-auto tw-px-6 lg:tw-px-16">
          <h2 className="tw-text-2xl tw-font-bold tw-mb-8">
            {" "}
            Exclusive Gifts & Service
          </h2>
          <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-8 tw-max-w-4xl tw-mx-auto tw-mb-12">
            {/* Gift Image - 1:1 aspect ratio with lightbox */}
            <div
              className="tw-aspect-square tw-bg-gray-100 tw-rounded-lg tw-overflow-hidden tw-relative tw-cursor-pointer"
              onClick={() =>
                setLightboxImage({
                  src: "https://images.unsplash.com/photo-1513883049090-d0b7439799bf?q=80&w=800&h=800&auto=format&fit=crop",
                  alt: "Gifts for each booking",
                })
              }
            >
              <img
                src="https://images.unsplash.com/photo-1513883049090-d0b7439799bf?q=80&w=800&h=800&auto=format&fit=crop"
                alt="Gifts for each booking"
                className="tw-object-cover tw-w-full tw-h-full"
              />
              {isEngineerMode && (
                <>
                  <label
                    htmlFor="gift-image-upload"
                    className="tw-absolute tw-inset-0 tw-cursor-pointer tw-flex tw-items-center tw-justify-center tw-opacity-0 hover:tw-opacity-50 tw-bg-black tw-text-white tw-transition-opacity"
                  >
                    Click to upload image
                  </label>
                  <input
                    type="file"
                    id="gift-image-upload"
                    accept="image/*"
                    className="tw-hidden"
                  />
                </>
              )}
            </div>

            {/* Gift Description - Editable text area */}
            <div className="tw-p-4 tw-border tw-border-gray-200 tw-rounded-lg tw-bg-gray-900">
              <h3 className="tw-text-xl tw-font-bold tw-mb-4 tw-text-[#FFD700]">
                Gift Description
              </h3>
              <div className="tw-text-white tw-min-h-[200px] tw-relative">
                <p>List of gifts for each booking:</p>
                <ul className="tw-list-disc tw-pl-5 tw-mt-4 tw-space-y-2">
                  <li>Mount Kinabalu climbing T-Shirt</li>
                  <li>Certificate Holder & postcards</li>
                  <li>Lightbulb Keychain & Lanyard</li>
                  <li>Digital photo album (6months)</li>
                </ul>
                {isEngineerMode && (
                  <div className="tw-absolute tw-inset-0 tw-cursor-text tw-opacity-0 hover:tw-opacity-50 tw-bg-black tw-text-white tw-flex tw-items-center tw-justify-center tw-transition-opacity">
                    Click to edit text
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Additional Gift & Service Item - Text on left, image on right */}
          <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-8 tw-max-w-4xl tw-mx-auto">
            {/* Additional Gift Description - Editable text area */}
            <div className="tw-p-4 tw-border tw-border-gray-200 tw-rounded-lg tw-bg-gray-900">
              <h3 className="tw-text-xl tw-font-bold tw-mb-4 tw-text-[#FFD700]">
                Services Provide
              </h3>
              <div className="tw-text-white tw-min-h-[200px] tw-relative">
                <p>Extra services available for your climbing experience:</p>
                <ul className="tw-list-disc tw-pl-5 tw-mt-4 tw-space-y-2">
                  <li>Bilingual Tour Leader lead the whole journey</li>
                  <li>Camera Photography service during the climb</li>
                  <li>Pre-trip online briefing</li>
                  <li>Customer support</li>
                </ul>
                {isEngineerMode && (
                  <div className="tw-absolute tw-inset-0 tw-cursor-text tw-opacity-0 hover:tw-opacity-50 tw-bg-black tw-text-white tw-flex tw-items-center tw-justify-center tw-transition-opacity">
                    Click to edit text
                  </div>
                )}
              </div>
            </div>

            {/* Additional Gift Image - 1:1 aspect ratio with lightbox */}
            <div
              className="tw-aspect-square tw-bg-gray-100 tw-rounded-lg tw-overflow-hidden tw-relative tw-cursor-pointer"
              onClick={() =>
                setLightboxImage({
                  src: "https://images.unsplash.com/photo-1551632811-561732d1e306?q=80&w=800&h=800&auto=format&fit=crop",
                  alt: "Additional services",
                })
              }
            >
              <img
                src="https://images.unsplash.com/photo-1551632811-561732d1e306?q=80&w=800&h=800&auto=format&fit=crop"
                alt="Additional services"
                className="tw-object-cover tw-w-full tw-h-full"
              />
              {isEngineerMode && (
                <>
                  <label
                    htmlFor="additional-gift-image-upload"
                    className="tw-absolute tw-inset-0 tw-cursor-pointer tw-flex tw-items-center tw-justify-center tw-opacity-0 hover:tw-opacity-50 tw-bg-black tw-text-white tw-transition-opacity"
                  >
                    Click to upload image
                  </label>
                  <input
                    type="file"
                    id="additional-gift-image-upload"
                    accept="image/*"
                    className="tw-hidden"
                  />
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Package Info Section - UPDATED with centered heading and repositioned image */}
      <section
        id="packages"
        className="tw-py-16 tw-border-b tw-border-gray-100"
      >
        <div className="tw-container tw-mx-auto tw-px-6 lg:tw-px-16">
          <h2 className="tw-text-2xl tw-font-bold tw-mb-2 tw-text-center">
            Mount Kinabalu 3D2N (sabahpark) Tour Package
          </h2>
          <h3 className="tw-text-xl tw-font-bold tw-text-center tw-mb-8 tw-text-red-600">
            NOT 100% get slot
          </h3>

          {/* Centered image below the heading */}
          <div className="tw-max-w-3xl tw-mx-auto tw-mb-8">
            <div className="tw-aspect-video tw-relative tw-rounded-lg tw-overflow-hidden">
              <img
                src="https://images.unsplash.com/photo-1507041957456-9c397ce39c97?q=80&w=1080&h=720&auto=format&fit=crop"
                alt="Mount Kinabalu Package"
                className="tw-object-cover tw-w-full tw-h-full"
              />
              {isEngineerMode && (
                <>
                  <label
                    htmlFor="product-image-upload"
                    className="tw-absolute tw-inset-0 tw-cursor-pointer tw-flex tw-items-center tw-justify-center tw-opacity-0 hover:tw-opacity-50 tw-bg-black tw-text-white tw-transition-opacity"
                  >
                    Click to upload image
                  </label>
                  <input
                    type="file"
                    id="product-image-upload"
                    accept="image/*"
                    className="tw-hidden"
                  />
                </>
              )}
            </div>
          </div>

          <div className="tw-text-center tw-mb-12">
            <p className="tw-mb-6">
              The mountain hut in this package is under a government. It is the
              cheapest and the hardest to get mountain hut.
            </p>

            <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-8 tw-max-w-4xl tw-mx-auto tw-mb-8">
              {/* BENEFIT Box */}
              <div className="tw-bg-white tw-p-6 tw-rounded-lg tw-shadow-md">
                <h4 className="tw-font-bold tw-mb-4 tw-flex tw-items-center tw-justify-center tw-text-blue-600">
                  <ThumbsUp className="tw-mr-2" size={20} />
                  <span>BENEFIT</span>
                </h4>
                <ul className="tw-list-disc tw-pl-5 tw-text-left tw-text-gray-700">
                  <li>Lowest price compare to other package</li>
                  <li>You can stay overnight in the newer mountain huts</li>
                </ul>
              </div>

              {/* RISK Box */}
              <div className="tw-bg-white tw-p-6 tw-rounded-lg tw-shadow-md">
                <h4 className="tw-font-bold tw-mb-4 tw-flex tw-items-center tw-justify-center tw-text-red-600">
                  <AlertTriangle className="tw-mr-2" size={20} />
                  <span>RISK</span>
                </h4>
                <ul className="tw-list-disc tw-pl-5 tw-text-left tw-text-gray-700">
                  <li>
                    Not 100% to get permit, you fight for it, try your luck
                  </li>
                  <li>
                    Do not purchase flight ticket before permit is success
                    obtain
                  </li>
                  <li>Every year the slot are finish up quickly.</li>
                </ul>
              </div>
            </div>

            {/* Pre-booking text and button centered */}
            <div className="tw-max-w-2xl tw-mx-auto tw-text-center">
              <p className="tw-mb-6 tw-text-gray-700">
                Pre-booking (not 100% secure slot, need to depend the permit
                application on year end)
              </p>
              <button
                className="tw-bg-[#00bfff] tw-text-white tw-px-8 tw-py-3 tw-rounded tw-font-bold hover:tw-bg-blue-600 tw-transition-colors"
                onClick={() => window.open("https://sotravel.com/trips/260")}
              >
                Book Now
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Customer Experience Map Section - UPDATED with black background and improved layout */}
      <section className="tw-py-16 tw-border-b tw-border-gray-100 tw-bg-black tw-text-white">
        <div className="tw-container tw-mx-auto tw-px-6 lg:tw-px-16">
          <h2 className="tw-text-2xl tw-font-bold tw-text-center tw-mb-4">
            Register Procedure
          </h2>

          {/* Journey text between title and map */}
          <div className="tw-flex tw-items-center tw-justify-center tw-mb-12">
            <AlertCircle size={20} className="tw-text-[#FFD700] tw-mr-2" />
            <p className="tw-text-[#FFD700] tw-font-medium">
              must read before booking
            </p>
          </div>

          <div className="tw-max-w-5xl tw-mx-auto">
            {/* Timeline with improved visual experience */}
            <div className="tw-flex tw-flex-col md:tw-flex-row tw-justify-between tw-items-start tw-mb-8 tw-relative">
              {/* Connection Line */}
              <div className="tw-hidden md:tw-block tw-absolute tw-top-10 tw-left-10 tw-right-10 tw-h-0.5 tw-bg-gray-700 tw-z-0"></div>

              {/* Steps */}
              {[
                {
                  text: "Choose tour date,make a pre-book,pay a deposit",
                  date: "Now",
                },
                {
                  text: "Wait for the mountain permit is open to apply,and to know is success or failure",
                  date: "Oct~Dec",
                },
                {
                  text: "Received our inform,add to chat group, pre-trip briefing,flight ticket purchase info",
                  date: "Oct~Dec(if success)",
                },
                {
                  text: "Tour leader briefing, clear payment balance",
                  date: "90days before",
                },
                {
                  text: "The journey begins and meet the tour leader",
                  date: "Tour date",
                },
              ].map((step, index) => (
                <div
                  key={index}
                  className="tw-flex tw-flex-col tw-items-center tw-mb-6 md:tw-mb-0 tw-z-10 tw-w-full md:tw-w-1/5"
                >
                  <div className="tw-bg-[#FFD700] tw-text-black tw-rounded-full tw-w-12 tw-h-12 tw-flex tw-items-center tw-justify-center tw-font-bold tw-mb-3">
                    {index + 1}
                  </div>
                  <div className="tw-text-[#FFD700] tw-text-sm tw-mb-2">
                    {step.date}
                  </div>
                  <div className="tw-bg-gray-900 tw-p-4 tw-rounded-lg tw-shadow-md tw-text-center tw-h-auto md:tw-h-36 tw-flex tw-items-center tw-justify-center">
                    <p className="tw-text-sm">{step.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section - UPDATED */}
      <section
        id="faq"
        className="tw-py-16 tw-border-b tw-border-gray-100 tw-bg-gray-50"
      >
        <div className="tw-container tw-mx-auto tw-px-6 lg:tw-px-16">
          <h2 className="tw-text-2xl tw-font-bold tw-text-center tw-mb-12">
            FAQ
          </h2>
          <div className="tw-max-w-3xl tw-mx-auto">
            {faqs.map((faq, index) => (
              <div key={index} className="tw-mb-4">
                <button
                  onClick={() => toggleAccordion(index)}
                  className="tw-w-full tw-flex tw-justify-between tw-items-center tw-p-4 tw-bg-white tw-rounded-lg tw-border tw-border-gray-200 tw-text-left"
                >
                  <span className="tw-font-medium">{faq.question}</span>
                  <ChevronDown
                    className={`tw-transition-transform ${
                      activeAccordion === index ? "tw-rotate-180" : ""
                    }`}
                    size={20}
                  />
                </button>
                {activeAccordion === index && (
                  <div className="tw-p-4 tw-bg-white tw-border-t-0 tw-border tw-border-gray-200 tw-rounded-b-lg">
                    <p className="tw-text-gray-700">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Limited Promotion Section - UPDATED with improved design */}
      <section className="tw-py-16 tw-border-b tw-border-gray-100 tw-bg-black tw-text-white">
        <div className="tw-container tw-mx-auto tw-px-6 lg:tw-px-16">
          <h2 className="tw-text-3xl tw-font-bold tw-text-center tw-mb-8 tw-tracking-wide">
            LIMITED PROMOTION
          </h2>
          <div className="tw-max-w-3xl tw-mx-auto">
            {/* Clickable promotion thumbnail image */}
            <div className="tw-relative tw-mb-8">
              <div
                className="tw-aspect-[16/9] tw-bg-gray-800 tw-rounded-lg tw-overflow-hidden tw-relative tw-cursor-pointer tw-group"
                onClick={() =>
                  setLightboxImage({
                    src: "https://images.unsplash.com/photo-1526772662000-3f88f10405ff?q=80&w=1080&h=720&auto=format&fit=crop",
                    alt: "Limited Time Promotion",
                  })
                }
              >
                <img
                  src="https://images.unsplash.com/photo-1526772662000-3f88f10405ff?q=80&w=1080&h=720&auto=format&fit=crop"
                  alt="Limited Time Promotion"
                  className="tw-object-cover tw-w-full tw-h-full tw-opacity-80 group-hover:tw-opacity-100 tw-transition-opacity"
                />
                <div className="tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center tw-bg-black tw-bg-opacity-40 group-hover:tw-bg-opacity-20 tw-transition-all">
                  <div className="tw-bg-black tw-bg-opacity-60 tw-p-3 tw-rounded-full">
                    <Search size={24} className="tw-text-white" />
                  </div>
                </div>
              </div>
              <div className="tw-absolute tw-bottom-4 tw-right-4 tw-bg-[#FFD700] tw-text-black tw-px-3 tw-py-1 tw-rounded-md tw-font-medium tw-text-sm">
                Click to view
              </div>
            </div>

            {/* Text box below the image with improved typography */}
            <div className="tw-p-8 tw-bg-gray-900 tw-rounded-lg tw-text-center">
              <h3 className="tw-text-xl tw-font-bold tw-mb-4 tw-text-[#FFD700]">
                Limited Time Offer - Book Now!
              </h3>
              <div className="tw-space-y-3 tw-text-lg">
                <p className="tw-text-white">
                  Early Bird:{" "}
                  <span className="tw-font-semibold">
                    January 1 - February 28, 2026
                  </span>
                </p>
                <p className="tw-text-white">
                  Mid Bird:{" "}
                  <span className="tw-font-semibold">
                    March 1 - April 30, 2026
                  </span>
                </p>
                <p className="tw-text-white">
                  Late Bird:{" "}
                  <span className="tw-font-semibold">
                    May 1 - June 30, 2026
                  </span>
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Book Now Section - UPDATED */}
      <section id="booking" className="tw-py-16 tw-border-b tw-border-gray-100">
        <div className="tw-container tw-mx-auto tw-px-6 lg:tw-px-16">
          <h2 className="tw-text-3xl tw-font-bold tw-text-center tw-mb-12">
            BOOK NOW
          </h2>

          {/* Added image below the heading */}
          <div className="tw-max-w-3xl tw-mx-auto tw-mb-8 tw-aspect-[16/9] tw-relative">
            <img
              src="/images/mountain-summit.jpeg"
              alt="Mount Kinabalu Booking"
              className="tw-object-cover tw-rounded-lg tw-w-full tw-h-full"
            />
            {isEngineerMode && (
              <>
                <label
                  htmlFor="booking-image-upload"
                  className="tw-absolute tw-inset-0 tw-cursor-pointer tw-flex tw-items-center tw-justify-center tw-opacity-0 hover:tw-opacity-50 tw-bg-black tw-text-white tw-transition-opacity"
                >
                  Click to upload booking image
                </label>
                <input
                  type="file"
                  id="booking-image-upload"
                  accept="image/*"
                  className="tw-hidden"
                />
              </>
            )}
          </div>

          <div className="tw-bg-gray-100 tw-p-8 tw-rounded-lg tw-mb-8 tw-max-w-3xl tw-mx-auto tw-text-center">
            <h3 className="tw-text-xl tw-font-bold tw-mb-4">
              Mount Kinabalu 3D2N (Sabahpark Package)
            </h3>
            <p className="tw-mb-4">
              So, are you ready for your first Mount Kinabalu Climb with us?
            </p>
          </div>

          <div className="tw-flex tw-flex-col tw-gap-4 tw-max-w-md tw-mx-auto">
            <button
              onClick={() => window.open("https://sotravel.com/trips/260")}
              className="tw-bg-[#00bfff] tw-text-white tw-px-6 tw-py-3 tw-rounded tw-font-bold hover:tw-bg-blue-600 tw-transition-colors"
            >
              Reserve your spot now
            </button>
            <button
              onClick={() => window.open("https://wa.me/601169871238")}
              className="tw-bg-[#00bfff] tw-text-white tw-px-6 tw-py-3 tw-rounded tw-font-bold hover:tw-bg-blue-600 tw-transition-colors"
            >
              Group or private booking
            </button>
          </div>

          {/* New section - No Risk Option */}
          <div className="tw-mt-12 tw-max-w-md tw-mx-auto tw-text-center">
            <h4 className="tw-text-lg tw-font-bold tw-mb-3">
              For those looking to secure a spot onto a confirmed trip, you can
              check out our premium Mt KK services.
            </h4>
            <button className="tw-bg-gray-300 tw-text-gray-700 tw-px-6 tw-py-3 tw-rounded tw-font-bold hover:tw-bg-gray-400 tw-transition-colors">
              Coming soon
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="tw-bg-black tw-text-white tw-py-12">
        <div className="tw-container tw-mx-auto tw-px-6 lg:tw-px-16">
          <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-3 tw-gap-8">
            <div>
              <h3 className="tw-text-xl tw-font-bold tw-mb-4">Contact Us</h3>
              <p className="tw-mb-2">Email: avidercrew@gmail.com</p>
              <p className="tw-mb-2">Whatsapp: +60 11 6987 1238</p>
              <p className="tw-mb-2">Whatsapp: +60 17 591 1238</p>
              <p>
                Address: 21,1st Floor, Lorong Bagan Tambun, Taman Bagan Tambun,
                14100 Simpang Ampat, Pulau Pinang
              </p>
            </div>
            <div>
              <h3 className="tw-text-xl tw-font-bold tw-mb-4">Quick Links</h3>
              <ul className="tw-space-y-2">
                <li>
                  <a
                    href="#"
                    className="hover:tw-text-[#00bfff] tw-transition-colors"
                  >
                    Home
                  </a>
                </li>
                <li>
                  <a
                    href="#about"
                    className="hover:tw-text-[#00bfff] tw-transition-colors"
                  >
                    About
                  </a>
                </li>
                <li>
                  <a
                    href="#packages"
                    className="hover:tw-text-[#00bfff] tw-transition-colors"
                  >
                    Packages
                  </a>
                </li>
                <li>
                  <a
                    href="#booking"
                    className="hover:tw-text-[#00bfff] tw-transition-colors"
                  >
                    Booking
                  </a>
                </li>
                <li>
                  <a
                    href="#faq"
                    className="hover:tw-text-[#00bfff] tw-transition-colors"
                  >
                    FAQ
                  </a>
                </li>
              </ul>
            </div>
            <div>
              <h3 className="tw-text-xl tw-font-bold tw-mb-4">Follow Us</h3>
              <div className="tw-flex tw-space-x-4">
                <a
                  href="https://www.facebook.com/avider.com.my"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:tw-text-[#00bfff] tw-transition-colors"
                >
                  Facebook
                </a>
                <a
                  href="https://www.instagram.com/avider_official/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:tw-text-[#00bfff] tw-transition-colors"
                >
                  Instagram
                </a>
                <a
                  href="https://www.youtube.com/@avider_official"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:tw-text-[#00bfff] tw-transition-colors"
                >
                  YouTube
                </a>
              </div>
            </div>
          </div>
          <div className="tw-mt-8 tw-pt-8 tw-border-t tw-border-gray-700 tw-text-center">
            <p>Avider powered by Sotravel</p>
          </div>
        </div>
      </footer>

      {/* WhatsApp Floating Button */}
      <a
        href="https://wa.me/601169871238"
        target="_blank"
        rel="noopener noreferrer"
        className="tw-fixed tw-bottom-6 tw-right-6 tw-bg-green-500 tw-text-white tw-p-4 tw-rounded-full tw-shadow-lg hover:tw-bg-green-600 tw-transition-colors tw-z-50"
        aria-label="Chat on WhatsApp"
      >
        <MessageCircle size={24} />
      </a>

      {/* Lightbox Modal */}
      {lightboxImage && (
        <div
          className="tw-fixed tw-inset-0 tw-bg-black tw-bg-opacity-80 tw-z-50 tw-flex tw-items-center tw-justify-center tw-p-4"
          onClick={() => setLightboxImage(null)}
        >
          <div className="tw-relative tw-max-w-5xl tw-max-h-[90vh] tw-w-full">
            <button
              onClick={() => setLightboxImage(null)}
              className="tw-absolute -tw-top-10 tw-right-0 tw-text-white hover:tw-text-gray-300"
              aria-label="Close lightbox"
            >
              <X size={24} />
            </button>
            <div className="tw-relative tw-aspect-video">
              <img
                src={lightboxImage.src || "/placeholder.svg"}
                alt={lightboxImage.alt}
                className="tw-object-contain tw-w-full tw-h-full"
              />
            </div>
          </div>
        </div>
      )}

      {/* Custom CSS for Manrope font */}
      <style jsx global>{`
        @import url("https://fonts.googleapis.com/css2?family=Manrope:wght@400;500;600;700&display=swap");
      `}</style>
    </div>
  );
};
